/**
 * Controllable Confirmation Dialog
 */

import React from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	Button,
	Typography,
	Box,
	IconButton,
	TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { alpha } from "@mui/material/styles";

type ControllableConfirmationProps = {
	open: boolean;
	title: string;
	content: string;
	onClose: () => void;
	onConfirm: () => Promise<void> | void;
	loading?: boolean;
	severity?: "info" | "warning" | "error" | "success";
	confirmLabel?: string;
	cancelLabel?: string;
	confirmationText?: string;
	confirmationPlaceholder?: string;
	showCloseButton?: boolean;
	children?: React.ReactNode;
};

const ControllableConfirmation: React.FC<ControllableConfirmationProps> = ({
	open,
	title,
	content,
	onClose,
	onConfirm,
	loading = false,
	severity = "info",
	confirmLabel,
	cancelLabel,
	confirmationText,
	confirmationPlaceholder,
	showCloseButton = true,
	children,
}) => {
	const [confirmation, setConfirmation] = React.useState("");
	const confirmationRef = React.useRef<HTMLDivElement | null>(null);

	const getSeverityColor = () => {
		switch (severity) {
			case "error":
				return "error";
			case "warning":
				return "warning";
			case "success":
				return "success";
			default:
				return "primary";
		}
	};

	const getSeverityIcon = () => {
		const iconStyle = { fontSize: 40 };
		switch (severity) {
			case "error":
				return <ErrorOutlineIcon color="error" sx={iconStyle} />;
			case "warning":
				return <WarningAmberIcon color="warning" sx={iconStyle} />;
			case "success":
				return <CheckCircleOutlineIcon color="success" sx={iconStyle} />;
			default:
				return <InfoOutlinedIcon color="primary" sx={iconStyle} />;
		}
	};

	return (
		<Dialog
			open={open}
			fullWidth
			maxWidth="xs"
			PaperProps={{
				sx: {
					borderRadius: 3,
					padding: 1,
				},
			}}
		>
			{showCloseButton && (
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						position: "absolute",
						right: 12,
						top: 12,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			)}
			<DialogContent sx={{ mt: 2 }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						textAlign: "center",
						gap: 2,
					}}
				>
					<Box
						sx={{
							width: 80,
							height: 80,
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: (theme) =>
								alpha(theme.palette[getSeverityColor()].main, 0.1),
							mb: 1,
						}}
					>
						{getSeverityIcon()}
					</Box>
					<Typography variant="h6" fontWeight={700}>
						{title}
					</Typography>
					<Typography color="text.secondary" variant="body1">
						{content}
					</Typography>
					{children}

					{confirmationText && (
						<TextField
							fullWidth
							label={"Баталгаажуулах"}
							variant="outlined"
							margin="normal"
							onChange={(e) => setConfirmation(e.target.value)}
							value={confirmation}
							placeholder={confirmationPlaceholder ?? "баталгаажуулах текст"}
							autoFocus
							ref={confirmationRef}
							sx={{ mt: 2 }}
						/>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ p: 2, gap: 1 }}>
				<Button
					onClick={onClose}
					fullWidth
					variant="outlined"
					color="inherit"
					sx={{ borderRadius: 2 }}
				>
					{cancelLabel ?? "Цуцлах"}
				</Button>
				<LoadingButton
					onClick={onConfirm}
					loading={loading}
					fullWidth
					variant="contained"
					color={getSeverityColor()}
					disabled={confirmationText ? confirmation !== confirmationText : false}
					sx={{
						borderRadius: 2,
						boxShadow: (theme) =>
							`0 4px 12px ${alpha(theme.palette[getSeverityColor()].main, 0.3)}`,
					}}
				>
					{confirmLabel ?? "Батлах"}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default ControllableConfirmation;
